import { z } from "zod";
import { email, isoDate, optionalEmail, optionalText, phoneInput, text, uuid } from "./common";
import { PERMISSIONS, type Permission } from "./permissions";

export const roleInput = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]{1,49}$/, "Lowercase letters, digits and underscores"),
  name: text(100),
  description: optionalText(500),
  permissions: z.array(z.enum(PERMISSIONS)).max(PERMISSIONS.length),
});
export const updateRoleInput = roleInput.omit({ key: true });

export interface Role {
  id: string;
  key: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  isOwner: boolean;
  permissions: Permission[];
  memberCount: number;
}

export const roleAssignment = z.object({ roleId: uuid, branchId: uuid.nullable().default(null) });

export const staffInput = z.object({
  fullName: text(200),
  designation: optionalText(100),
  phone: phoneInput,
  email: optionalEmail,
  gender: z.enum(["female", "male", "other", "undisclosed"]).nullable().default(null),
  joinedOn: isoDate.nullable().default(null),
  status: z.enum(["active", "inactive", "left"]).default("active"),
  notes: optionalText(2000),
  branchIds: z.array(uuid).max(200).default([]),
});
export const updateStaffInput = staffInput.extend({ version: z.number().int().min(1) });

/** Give a staff member a login: creates/links a user and a membership, returns a one-time invite link. */
export const inviteStaffInput = z.object({
  email,
  roles: z.array(roleAssignment).min(1).max(20),
});
export const setMemberRolesInput = z.object({ roles: z.array(roleAssignment).min(1).max(20) });

export interface StaffMember {
  id: string;
  fullName: string;
  designation: string | null;
  phone: string | null;
  email: string | null;
  gender: string | null;
  joinedOn: string | null;
  status: "active" | "inactive" | "left";
  notes: string | null;
  branchIds: string[];
  isDoctor: boolean;
  login: null | {
    membershipId: string;
    userId: string;
    email: string;
    status: "invited" | "active" | "suspended" | "removed";
    roles: { roleId: string; roleName: string; branchId: string | null }[];
  };
  version: number;
}

export interface InvitationResult {
  membershipId: string;
  /** Shown once to the admin so they can share it if email is not configured yet. */
  inviteUrl: string;
  expiresAt: string;
  emailSent: boolean;
}
